import { type ReactNode } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ClearIcon from '@material-ui/icons/Clear';
import LinkIcon from '@material-ui/icons/Link';
import { makeStyles } from '@material-ui/core/styles';
import { validateUrl, extractSlug } from './urlValidation';

// ── Brand icon SVG components ─────────────────────────────────────────────────

const SlackIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
    <path fill="#E01E5A" d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52z"/>
    <path fill="#E01E5A" d="M6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z"/>
    <path fill="#2EB67D" d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834z"/>
    <path fill="#2EB67D" d="M8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z"/>
    <path fill="#ECB22E" d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834z"/>
    <path fill="#ECB22E" d="M17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z"/>
    <path fill="#36C5F0" d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52z"/>
    <path fill="#36C5F0" d="M15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
  </svg>
);

const JiraIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
    <defs>
      <linearGradient id="jira-a" x1="99%" x2="35%" y1="15%" y2="72%">
        <stop offset="0%" stopColor="#0052CC"/>
        <stop offset="100%" stopColor="#2684FF"/>
      </linearGradient>
      <linearGradient id="jira-b" x1="1%" x2="65%" y1="85%" y2="28%">
        <stop offset="0%" stopColor="#0052CC"/>
        <stop offset="100%" stopColor="#2684FF"/>
      </linearGradient>
    </defs>
    <path fill="#2684FF" d="M11.53 2L.55 13l5.08 5 5.9-5.9L16.44 18l5.08-5L11.53 2z"/>
    <path fill="url(#jira-a)" d="M11.53 7.1L7.27 11.35l4.26 4.26 4.26-4.26L11.53 7.1z"/>
  </svg>
);

const ConfluenceIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
    <defs>
      <linearGradient id="conf-a" x1="99%" x2="46%" y1="14%" y2="57%">
        <stop offset="0%" stopColor="#0052CC"/>
        <stop offset="100%" stopColor="#2684FF"/>
      </linearGradient>
      <linearGradient id="conf-b" x1="1%" x2="54%" y1="86%" y2="43%">
        <stop offset="0%" stopColor="#0052CC"/>
        <stop offset="100%" stopColor="#2684FF"/>
      </linearGradient>
    </defs>
    <path fill="url(#conf-b)" d="M.87 18.4c-.23.38-.49.84-.67 1.13a.67.67 0 0 0 .23.91l3.43 2a.67.67 0 0 0 .91-.24c.17-.3.4-.7.64-1.11 1.82-3.05 3.65-2.68 6.97-.82l3.4 1.96a.66.66 0 0 0 .89-.25l1.98-3.43a.66.66 0 0 0-.24-.91l-3.48-2.01c-1.07-.62-4.16-2.39-4.16-5.36 0-1.28.76-2.59 2.14-3.85"/>
    <path fill="url(#conf-a)" d="M23.13 5.6c.23-.38.49-.84.67-1.13a.67.67 0 0 0-.23-.91l-3.43-2a.67.67 0 0 0-.91.24c-.17.3-.4.7-.64 1.11-1.82 3.05-3.65 2.68-6.97.82L8.22 1.77a.66.66 0 0 0-.89.25L5.35 5.45a.66.66 0 0 0 .24.91l3.48 2.01c1.07.62 4.16 2.39 4.16 5.36 0 1.28-.76 2.59-2.14 3.85"/>
  </svg>
);

const FigmaIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
    <path fill="#F24E1E" d="M8 24c2.208 0 4-1.792 4-4v-4H8c-2.208 0-4 1.792-4 4s1.792 4 4 4z"/>
    <path fill="#FF7262" d="M4 12c0-2.208 1.792-4 4-4h4v8H8c-2.208 0-4-1.792-4-4z"/>
    <path fill="#A259FF" d="M4 4c0-2.208 1.792-4 4-4h4v8H8C5.792 8 4 6.208 4 4z"/>
    <path fill="#1ABCFE" d="M12 0h4c2.208 0 4 1.792 4 4s-1.792 4-4 4h-4V0z"/>
    <path fill="#0ACF83" d="M20 12c0 2.208-1.792 4-4 4s-4-1.792-4-4 1.792-4 4-4 4 1.792 4 4z"/>
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────

export type ResourceLinkType =
  | 'slack'
  | 'jira'
  | 'confluence'
  | 'figma'
  | 'custom';

export type SlackSubType = 'public' | 'alerts_notifications';

export interface ResourceLink {
  type: ResourceLinkType;
  label: string;
  url: string;
  // Only meaningful when type === 'slack'.
  slackSubType?: SlackSubType;
  channelId?: string;
}

const TYPES: { value: ResourceLinkType; label: string; icon: ReactNode }[] = [
  { value: 'slack',      label: 'Slack',       icon: <SlackIcon /> },
  { value: 'jira',       label: 'Jira',        icon: <JiraIcon /> },
  { value: 'confluence', label: 'Confluence',  icon: <ConfluenceIcon /> },
  { value: 'figma',      label: 'Figma',       icon: <FigmaIcon /> },
  { value: 'custom',     label: 'Other',       icon: <LinkIcon style={{ verticalAlign: 'middle' }} /> },
];

const SLACK_SUB_TYPES: { value: SlackSubType; label: string }[] = [
  { value: 'public', label: 'Public' },
  { value: 'alerts_notifications', label: 'Alerts & Notifications' },
];

const VALID_SLACK_SUB_TYPES = new Set(SLACK_SUB_TYPES.map(t => t.value));

/**
 * At most one Public and one Alerts & Notifications slack link is allowed
 * per team. Returns the set of slack sub-types that appear on 2+ rows —
 * every row with one of these sub-types is a duplicate, not just the
 * second occurrence.
 */
export function duplicateSlackSubTypes(links: ResourceLink[]): Set<SlackSubType> {
  const seen = new Set<SlackSubType>();
  const duplicates = new Set<SlackSubType>();
  for (const link of links) {
    const subType = link.type === 'slack' ? link.slackSubType : undefined;
    if (!subType) continue;
    if (seen.has(subType)) duplicates.add(subType);
    seen.add(subType);
  }
  return duplicates;
}

/** channelId is slack-only — extractSlug also parses Jira/Confluence, so
 * gate it here rather than let a non-slack row pick up an unrelated value. */
function slackChannelId(type: ResourceLinkType, url: string): string | undefined {
  return type === 'slack' ? extractSlug('slack', url) : undefined;
}

/** True if any row has an invalid URL, a slack row is missing its required
 * sub-type, or two slack rows share the same sub-type. Used to gate Save on
 * the team create/edit forms. */
export function hasResourceLinkErrors(links: ResourceLink[]): boolean {
  const hasMissingRequiredField = links.some(
    link => !link.url.trim() || (link.type === 'custom' && !link.label.trim()),
  );
  const hasUrlError = links.some(link => !!validateUrl(link.type, link.url));
  const hasInvalidSlackSubType = links.some(
    link =>
      link.type === 'slack' &&
      // Not just missing — a malformed pre-existing value (e.g. loaded from
      // YAML) can slip past a bare truthiness check and only get caught
      // late, by the backend, as a confusing 400.
      (!link.slackSubType || !VALID_SLACK_SUB_TYPES.has(link.slackSubType)),
  );
  return (
    hasMissingRequiredField ||
    hasUrlError ||
    hasInvalidSlackSubType ||
    duplicateSlackSubTypes(links).size > 0
  );
}

const useStyles = makeStyles(theme => ({
  row: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    background: theme.palette.background.paper,
  },
  iconLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
}));

export interface ResourceLinkEditorProps {
  value: ResourceLink[];
  onChange: (links: ResourceLink[]) => void;
}

export function ResourceLinkEditor({ value, onChange }: ResourceLinkEditorProps) {
  const classes = useStyles();

  const update = (index: number, partial: Partial<ResourceLink>) => {
    const updated = value.map((link, i) =>
      i === index ? { ...link, ...partial } : link,
    );
    onChange(updated);
  };

  const add = () => {
    onChange([...value, { type: 'slack', label: '', url: '' }]);
  };

  const remove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const duplicateSubTypes = duplicateSlackSubTypes(value);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Resource Links
      </Typography>
      <Typography variant="caption" color="textSecondary" gutterBottom>
        A team may have at most two Slack links — one Public (shown on the team page)
        and one Alerts & Notifications. The Alerts & Notifications channel is never
        shown on the team page; it's metadata read by other teams' background agents
        (e.g. productivity, security) to route notifications to the right channel.
      </Typography>

      {value.map((link, i) => {
        const urlError = validateUrl(link.type, link.url);
        const typeInfo = TYPES.find(t => t.value === link.type)!;
        const duplicateSlackSubType =
          link.slackSubType && duplicateSubTypes.has(link.slackSubType)
            ? link.slackSubType
            : undefined;
        return (
          <Box key={i} className={classes.row}>
            <Grid container spacing={1} alignItems="flex-start">
              {/* Type selector */}
              <Grid item xs={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Type"
                  value={link.type}
                  onChange={e => {
                    const type = e.target.value as ResourceLinkType;
                    update(i, {
                      type,
                      channelId: slackChannelId(type, link.url),
                      // The Label input is hidden for every type except
                      // "Other" — clear it when switching away so a stale
                      // value can't silently become the persisted title
                      // (see defaultLinkTitle in the backend yaml.ts).
                      label: type === 'custom' ? link.label : '',
                    });
                  }}
                  variant="outlined"
                >
                  {TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>
                      <span className={classes.iconLabel}>
                        {t.icon}
                        {t.label}
                      </span>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Label — only shown for the "Other" type; every other type is
                  identified by its sub-type/icon instead. */}
              {link.type === 'custom' && (
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Label"
                    required
                    value={link.label}
                    onChange={e => update(i, { label: e.target.value })}
                    variant="outlined"
                    placeholder={`${typeInfo.label} channel`}
                  />
                </Grid>
              )}

              {/* Slack sub-type — required, determines team-page visibility */}
              {link.type === 'slack' && (
                <Grid item xs={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Sub-type"
                    required
                    error={!link.slackSubType || !!duplicateSlackSubType}
                    helperText={duplicateSlackSubType ? 'Only one link per sub-type is allowed' : undefined}
                    value={link.slackSubType ?? ''}
                    onChange={e => update(i, { slackSubType: e.target.value as SlackSubType })}
                    variant="outlined"
                  >
                    {SLACK_SUB_TYPES.map(t => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {/* URL */}
              <Grid item xs={link.type === 'custom' || link.type === 'slack' ? 5 : 8}>
                <Tooltip title={urlError ?? ''} open={!!urlError} placement="top">
                  <TextField
                    fullWidth
                    size="small"
                    label="URL"
                    required
                    value={link.url}
                    onChange={e => {
                      const url = e.target.value;
                      update(i, { url, channelId: slackChannelId(link.type, url) });
                    }}
                    variant="outlined"
                    error={!!urlError}
                    helperText={urlError}
                    placeholder={
                      link.type === 'slack'
                        ? 'https://arrive.enterprise.slack.com/archives/C…'
                        : 'https://…'
                    }
                  />
                </Tooltip>
              </Grid>

              {/* Remove */}
              <Grid item xs={1} style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton size="small" onClick={() => remove(i)} aria-label="Remove link">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        );
      })}

      <Button
        startIcon={<AddIcon />}
        onClick={add}
        size="small"
        variant="text"
        color="primary"
      >
        Add Resource Link
      </Button>
    </Box>
  );
}
